import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Globe, Users, Building2, ArrowRight, ArrowLeft, Check, Upload, MapPin, Briefcase, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import WalletConnectModal from '@/components/WalletConnectModal';

const causes = ['Education', 'Healthcare', 'Environment', 'Hunger Relief', 'Housing', 'Youth Development', 'Animal Welfare', 'Disaster Relief'];
const skills = ['Project Management', 'Teaching/Tutoring', 'Fundraising', 'Web Development', 'Photography', 'Grant Writing', 'Medical', 'Legal Aid', 'Marketing', 'Logistics'];
const sponsorInterests = ['Education Programs', 'Clean Energy', 'Healthcare Access', 'Community Development', 'Tech for Good', 'Disaster Relief', 'DEI Initiatives', 'Employee Volunteering'];

const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? 300 : -300, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? -300 : 300, opacity: 0 }),
};

function StepIndicator({ current, total }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-8" data-testid="step-indicator">
      {Array.from({ length: total }, (_, i) => (
        <div key={i} className={`h-2 rounded-full transition-all duration-300 ${i === current ? 'w-8 bg-gradient-to-r from-[#7C3AED] to-[#2563EB]' : i < current ? 'w-2 bg-[#7C3AED]' : 'w-2 bg-gray-200'}`} data-testid={`step-dot-${i}`} />
      ))}
    </div>
  );
}

function ChipSelector({ items, selected, onToggle, testIdPrefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map(item => (
        <button key={item} onClick={() => onToggle(item)} className={`px-3.5 py-2 rounded-full text-sm font-body transition-all ${selected.includes(item) ? 'bg-gradient-to-r from-[#7C3AED] to-[#2563EB] text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-testid={`${testIdPrefix}-${item.toLowerCase().replace(/\s+/g, '-')}`}>
          {selected.includes(item) && <Check className="w-3 h-3 inline mr-1.5" />}{item}
        </button>
      ))}
    </div>
  );
}

/* ======================== DONOR PATH (4 steps) ======================== */
function DonorPath({ step, data, setData }) {
  const [walletOpen, setWalletOpen] = useState(false);
  const toggleCause = (c) => setData(d => ({ ...d, causes: d.causes?.includes(c) ? d.causes.filter(x => x !== c) : [...(d.causes || []), c] }));

  const mockProjects = [
    { title: '3,000 Meals for Tampa Bay', cause: 'Hunger Relief', funded: 85 },
    { title: 'Clean Water for Kigali Schools', cause: 'Education', funded: 62 },
    { title: 'Reforestation in Appalachia', cause: 'Environment', funded: 41 },
  ];

  if (step === 0) return (
    <div className="space-y-5" data-testid="donor-step-causes">
      <h2 className="text-xl font-heading font-semibold text-[#111827]">What causes matter to you?</h2>
      <p className="text-sm text-gray-500 font-body">Select one or more — we'll personalize your feed.</p>
      <ChipSelector items={causes} selected={data.causes || []} onToggle={toggleCause} testIdPrefix="cause" />
    </div>
  );
  if (step === 1) return (
    <div className="space-y-5" data-testid="donor-step-wallet">
      <h2 className="text-xl font-heading font-semibold text-[#111827]">Connect Your Wallet</h2>
      <p className="text-sm text-gray-500 font-body">Optional — enables on-chain donation receipts and tax tracking.</p>
      <div className="bg-gray-50 rounded-2xl p-6 text-center">
        {data.walletConnected ? (
          <div className="space-y-2">
            <div className="w-12 h-12 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto"><Check className="w-6 h-6 text-[#10B981]" /></div>
            <p className="font-heading font-medium text-[#111827]">Wallet Connected</p>
            <p className="text-xs font-mono text-gray-500">0xAb7F...3f9E</p>
          </div>
        ) : (
          <>
            <Wallet className="w-10 h-10 text-[#7C3AED] mx-auto mb-3" />
            <Button onClick={() => setWalletOpen(true)} className="bg-[#F6851B] hover:bg-[#E2761B] text-white rounded-full h-10 px-6 border-0" data-testid="onboard-wallet-btn">
              <Wallet className="w-4 h-4 mr-2" /> Connect MetaMask
            </Button>
            <p className="text-xs text-gray-400 mt-2 font-body">You can always do this later in Settings</p>
          </>
        )}
      </div>
      <WalletConnectModal open={walletOpen} onOpenChange={setWalletOpen} onConnected={() => setData(d => ({ ...d, walletConnected: true }))} />
    </div>
  );
  if (step === 2) return (
    <div className="space-y-5" data-testid="donor-step-profile">
      <h2 className="text-xl font-heading font-semibold text-[#111827]">Set Up Your Profile</h2>
      <p className="text-sm text-gray-500 font-body">How would you like to appear to others?</p>
      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium text-[#111827] font-heading block mb-1.5">Display Name</label>
          <Input value={data.displayName || ''} onChange={(e) => setData(d => ({ ...d, displayName: e.target.value }))} placeholder="Sarah Chen" className="rounded-xl border-gray-200 font-body" data-testid="onboard-display-name" />
        </div>
        <div>
          <label className="text-sm font-medium text-[#111827] font-heading block mb-1.5">Location (optional)</label>
          <Input value={data.location || ''} onChange={(e) => setData(d => ({ ...d, location: e.target.value }))} placeholder="San Francisco, CA" className="rounded-xl border-gray-200 font-body" data-testid="onboard-location" />
        </div>
        <div>
          <label className="text-sm font-medium text-[#111827] font-heading block mb-1.5">Privacy Level</label>
          <div className="grid grid-cols-3 gap-2">
            {['Public', 'Private', 'Anonymous'].map(p => (
              <button key={p} onClick={() => setData(d => ({ ...d, privacy: p }))} className={`px-3 py-2.5 rounded-xl text-sm font-body transition-all border-2 ${data.privacy === p ? 'border-transparent bg-gradient-to-r from-[#7C3AED] to-[#2563EB] text-white' : 'border-gray-100 bg-gray-50 text-gray-600 hover:border-gray-200'}`} data-testid={`privacy-${p.toLowerCase()}`}>
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
  return (
    <div className="space-y-5" data-testid="donor-step-welcome">
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#2563EB] flex items-center justify-center mx-auto mb-4"><Heart className="w-8 h-8 text-white" /></div>
        <h2 className="text-xl font-heading font-semibold text-[#111827]">Welcome to HumaClub!</h2>
        <p className="text-sm text-gray-500 font-body mt-1">Here are 3 projects we think you'll love.</p>
      </div>
      <div className="space-y-3">
        {mockProjects.map((p, i) => (
          <div key={i} className="bg-gray-50 rounded-xl p-4 flex items-center justify-between" data-testid={`recommended-project-${i}`}>
            <div>
              <p className="font-heading font-medium text-sm text-[#111827]">{p.title}</p>
              <p className="text-xs text-gray-500 font-body">{p.cause}</p>
            </div>
            <div className="text-right shrink-0 ml-3">
              <div className="w-16 h-1.5 rounded-full bg-gray-200 overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-[#7C3AED] to-[#2563EB]" style={{ width: `${p.funded}%` }} /></div>
              <p className="text-xs text-gray-400 mt-1 font-body">{p.funded}% funded</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ======================== NGO PATH (5 steps) ======================== */
function NGOPath({ step, data, setData }) {
  if (step === 0) return (
    <div className="space-y-5" data-testid="ngo-step-details">
      <h2 className="text-xl font-heading font-semibold text-[#111827]">Organization Details</h2>
      <p className="text-sm text-gray-500 font-body">Tell us about your organization.</p>
      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium text-[#111827] font-heading block mb-1.5">Organization Name</label>
          <Input value={data.orgName || ''} onChange={(e) => setData(d => ({ ...d, orgName: e.target.value }))} placeholder="Tampa Bay Outreach" className="rounded-xl border-gray-200 font-body" data-testid="ngo-org-name" />
        </div>
        <div>
          <label className="text-sm font-medium text-[#111827] font-heading block mb-1.5">Mission Statement</label>
          <textarea value={data.mission || ''} onChange={(e) => setData(d => ({ ...d, mission: e.target.value }))} placeholder="Brief description of your mission..." className="w-full rounded-xl border border-gray-200 font-body text-sm p-3 h-24 resize-none focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/20 focus:border-[#7C3AED]" data-testid="ngo-mission" />
        </div>
        <div>
          <label className="text-sm font-medium text-[#111827] font-heading block mb-1.5">Primary Cause Area</label>
          <ChipSelector items={causes.slice(0, 6)} selected={data.causes || []} onToggle={(c) => setData(d => ({ ...d, causes: [c] }))} testIdPrefix="ngo-cause" />
        </div>
      </div>
    </div>
  );
  if (step === 1) return (
    <div className="space-y-5" data-testid="ngo-step-upload">
      <h2 className="text-xl font-heading font-semibold text-[#111827]">501(c)(3) Verification</h2>
      <p className="text-sm text-gray-500 font-body">Upload your determination letter for verification.</p>
      <div className="border-2 border-dashed border-gray-200 rounded-2xl p-8 text-center hover:border-[#7C3AED]/40 transition-colors cursor-pointer" data-testid="ngo-upload-area">
        <Upload className="w-10 h-10 text-gray-300 mx-auto mb-3" />
        <p className="font-heading font-medium text-sm text-[#111827]">Drop your 501(c)(3) letter here</p>
        <p className="text-xs text-gray-400 font-body mt-1">PDF, JPG, PNG up to 10MB</p>
        <Button variant="outline" className="mt-4 rounded-full text-sm border-gray-200" data-testid="ngo-browse-btn">Browse Files</Button>
        {data.fileUploaded && <p className="text-sm text-[#10B981] mt-3 font-body"><Check className="w-4 h-4 inline mr-1" />501c3_letter.pdf uploaded</p>}
      </div>
      <button onClick={() => setData(d => ({ ...d, fileUploaded: true }))} className="text-xs text-[#7C3AED] hover:underline font-body" data-testid="ngo-mock-upload">Simulate upload for demo</button>
    </div>
  );
  if (step === 2) return (
    <div className="space-y-5" data-testid="ngo-step-humadash">
      <h2 className="text-xl font-heading font-semibold text-[#111827]">HumaDash Integration</h2>
      <p className="text-sm text-gray-500 font-body">Connect with HumaDash for real-time impact tracking.</p>
      <div className="bg-gradient-to-br from-[#7C3AED]/5 to-[#2563EB]/5 rounded-2xl p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#7C3AED] to-[#2563EB] flex items-center justify-center"><Globe className="w-5 h-5 text-white" /></div>
          <div>
            <p className="font-heading font-semibold text-sm text-[#111827]">HumaDash Portal</p>
            <p className="text-xs text-gray-500 font-body">Real-time transparency dashboard</p>
          </div>
        </div>
        <ul className="space-y-2 text-sm text-gray-600 font-body">
          <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#10B981] mt-0.5 shrink-0" /> Automated fund tracking</li>
          <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#10B981] mt-0.5 shrink-0" /> On-chain verification for every transaction</li>
          <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#10B981] mt-0.5 shrink-0" /> Donor-facing transparency widget</li>
        </ul>
        <Button onClick={() => setData(d => ({ ...d, humadashConnected: true }))} className={`w-full rounded-full h-10 border-0 transition-all ${data.humadashConnected ? 'bg-[#10B981] hover:bg-[#059669]' : 'bg-gradient-to-r from-[#7C3AED] to-[#2563EB] hover:shadow-lg'} text-white`} data-testid="ngo-humadash-btn">
          {data.humadashConnected ? <><Check className="w-4 h-4 mr-2" /> Connected</> : 'Connect HumaDash'}
        </Button>
      </div>
    </div>
  );
  if (step === 3) return (
    <div className="space-y-5" data-testid="ngo-step-contact">
      <h2 className="text-xl font-heading font-semibold text-[#111827]">Admin Contact</h2>
      <p className="text-sm text-gray-500 font-body">Primary contact for your organization on HumaClub.</p>
      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium text-[#111827] font-heading block mb-1.5">Admin Name</label>
          <Input value={data.adminName || ''} onChange={(e) => setData(d => ({ ...d, adminName: e.target.value }))} placeholder="Maria Santos" className="rounded-xl border-gray-200 font-body" data-testid="ngo-admin-name" />
        </div>
        <div>
          <label className="text-sm font-medium text-[#111827] font-heading block mb-1.5">Phone Number</label>
          <Input value={data.phone || ''} onChange={(e) => setData(d => ({ ...d, phone: e.target.value }))} placeholder="+1 (555) 123-4567" className="rounded-xl border-gray-200 font-body" data-testid="ngo-phone" />
        </div>
        <div>
          <label className="text-sm font-medium text-[#111827] font-heading block mb-1.5">Role / Title</label>
          <Input value={data.role || ''} onChange={(e) => setData(d => ({ ...d, role: e.target.value }))} placeholder="Executive Director" className="rounded-xl border-gray-200 font-body" data-testid="ngo-role" />
        </div>
      </div>
    </div>
  );
  return (
    <div className="space-y-5 text-center" data-testid="ngo-step-confirm">
      <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mx-auto"><Globe className="w-8 h-8 text-amber-500" /></div>
      <h2 className="text-xl font-heading font-semibold text-[#111827]">Application Under Review</h2>
      <p className="text-sm text-gray-500 font-body max-w-sm mx-auto">Our team will verify your 501(c)(3) status within 2-3 business days. You'll receive an email confirmation once approved.</p>
      <div className="bg-gray-50 rounded-xl p-4 text-left">
        <p className="text-xs text-gray-400 font-body mb-2">Submitted details</p>
        <p className="text-sm font-heading font-medium text-[#111827]">{data.orgName || 'Your Organization'}</p>
        <p className="text-xs text-gray-500 font-body">{data.adminName || 'Admin'} &middot; {data.causes?.[0] || 'General'}</p>
      </div>
    </div>
  );
}

/* ======================== VOLUNTEER PATH (3 steps) ======================== */
function VolunteerPath({ step, data, setData }) {
  const toggleSkill = (s) => setData(d => ({ ...d, skills: d.skills?.includes(s) ? d.skills.filter(x => x !== s) : [...(d.skills || []), s] }));

  const mockOpportunities = [
    { title: 'Weekend Food Prep — Station House BBQ', location: 'Tampa, FL', type: 'In-Person' },
    { title: 'Remote Grant Writing Support', location: 'Remote', type: 'Virtual' },
    { title: 'Youth Mentorship Program', location: 'Austin, TX', type: 'Hybrid' },
  ];

  if (step === 0) return (
    <div className="space-y-5" data-testid="vol-step-skills">
      <h2 className="text-xl font-heading font-semibold text-[#111827]">Skills & Interests</h2>
      <p className="text-sm text-gray-500 font-body">Select skills you'd like to contribute.</p>
      <ChipSelector items={skills} selected={data.skills || []} onToggle={toggleSkill} testIdPrefix="skill" />
    </div>
  );
  if (step === 1) return (
    <div className="space-y-5" data-testid="vol-step-location">
      <h2 className="text-xl font-heading font-semibold text-[#111827]">Location & Preferences</h2>
      <p className="text-sm text-gray-500 font-body">Help us match you with nearby opportunities.</p>
      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium text-[#111827] font-heading block mb-1.5">Your Location</label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input value={data.location || ''} onChange={(e) => setData(d => ({ ...d, location: e.target.value }))} placeholder="City, State" className="rounded-xl border-gray-200 font-body pl-10" data-testid="vol-location" />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium text-[#111827] font-heading block mb-1.5">Availability</label>
          <div className="grid grid-cols-3 gap-2">
            {['Weekdays', 'Weekends', 'Flexible'].map(a => (
              <button key={a} onClick={() => setData(d => ({ ...d, availability: a }))} className={`px-3 py-2.5 rounded-xl text-sm font-body transition-all border-2 ${data.availability === a ? 'border-transparent bg-gradient-to-r from-[#7C3AED] to-[#2563EB] text-white' : 'border-gray-100 bg-gray-50 text-gray-600 hover:border-gray-200'}`} data-testid={`avail-${a.toLowerCase()}`}>
                {a}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-sm font-medium text-[#111827] font-heading block mb-1.5">Preferred Format</label>
          <div className="grid grid-cols-3 gap-2">
            {['In-Person', 'Virtual', 'Either'].map(f => (
              <button key={f} onClick={() => setData(d => ({ ...d, format: f }))} className={`px-3 py-2.5 rounded-xl text-sm font-body transition-all border-2 ${data.format === f ? 'border-transparent bg-gradient-to-r from-[#7C3AED] to-[#2563EB] text-white' : 'border-gray-100 bg-gray-50 text-gray-600 hover:border-gray-200'}`} data-testid={`format-${f.toLowerCase()}`}>
                {f}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
  return (
    <div className="space-y-5" data-testid="vol-step-welcome">
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#2563EB] flex items-center justify-center mx-auto mb-4"><Users className="w-8 h-8 text-white" /></div>
        <h2 className="text-xl font-heading font-semibold text-[#111827]">Welcome, Volunteer!</h2>
        <p className="text-sm text-gray-500 font-body mt-1">Here are opportunities matching your skills.</p>
      </div>
      <div className="space-y-3">
        {mockOpportunities.map((o, i) => (
          <div key={i} className="bg-gray-50 rounded-xl p-4 flex items-center justify-between" data-testid={`matched-opp-${i}`}>
            <div>
              <p className="font-heading font-medium text-sm text-[#111827]">{o.title}</p>
              <p className="text-xs text-gray-500 font-body flex items-center gap-1"><MapPin className="w-3 h-3" />{o.location}</p>
            </div>
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${o.type === 'Virtual' ? 'bg-blue-50 text-blue-600' : o.type === 'Hybrid' ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}`}>{o.type}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ======================== CORPORATE PATH (4 steps) ======================== */
function CorporatePath({ step, data, setData }) {
  const toggleInterest = (s) => setData(d => ({ ...d, interests: d.interests?.includes(s) ? d.interests.filter(x => x !== s) : [...(d.interests || []), s] }));

  if (step === 0) return (
    <div className="space-y-5" data-testid="corp-step-details">
      <h2 className="text-xl font-heading font-semibold text-[#111827]">Company Details</h2>
      <p className="text-sm text-gray-500 font-body">Tell us about your organization.</p>
      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium text-[#111827] font-heading block mb-1.5">Company Name</label>
          <Input value={data.companyName || ''} onChange={(e) => setData(d => ({ ...d, companyName: e.target.value }))} placeholder="TechCorp" className="rounded-xl border-gray-200 font-body" data-testid="corp-company" />
        </div>
        <div>
          <label className="text-sm font-medium text-[#111827] font-heading block mb-1.5">Industry</label>
          <Input value={data.industry || ''} onChange={(e) => setData(d => ({ ...d, industry: e.target.value }))} placeholder="Technology" className="rounded-xl border-gray-200 font-body" data-testid="corp-industry" />
        </div>
        <div>
          <label className="text-sm font-medium text-[#111827] font-heading block mb-1.5">Company Size</label>
          <div className="grid grid-cols-3 gap-2">
            {['1-50', '51-500', '500+'].map(s => (
              <button key={s} onClick={() => setData(d => ({ ...d, size: s }))} className={`px-3 py-2.5 rounded-xl text-sm font-body transition-all border-2 ${data.size === s ? 'border-transparent bg-gradient-to-r from-[#7C3AED] to-[#2563EB] text-white' : 'border-gray-100 bg-gray-50 text-gray-600 hover:border-gray-200'}`} data-testid={`size-${s}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
  if (step === 1) return (
    <div className="space-y-5" data-testid="corp-step-interests">
      <h2 className="text-xl font-heading font-semibold text-[#111827]">Sponsorship Interests</h2>
      <p className="text-sm text-gray-500 font-body">Which impact areas align with your ESG goals?</p>
      <ChipSelector items={sponsorInterests} selected={data.interests || []} onToggle={toggleInterest} testIdPrefix="sponsor" />
    </div>
  );
  if (step === 2) return (
    <div className="space-y-5" data-testid="corp-step-contact">
      <h2 className="text-xl font-heading font-semibold text-[#111827]">Enterprise Contact</h2>
      <p className="text-sm text-gray-500 font-body">Who should we reach out to?</p>
      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium text-[#111827] font-heading block mb-1.5">Contact Name</label>
          <Input value={data.contactName || ''} onChange={(e) => setData(d => ({ ...d, contactName: e.target.value }))} placeholder="David Park" className="rounded-xl border-gray-200 font-body" data-testid="corp-contact-name" />
        </div>
        <div>
          <label className="text-sm font-medium text-[#111827] font-heading block mb-1.5">Title / Role</label>
          <Input value={data.contactTitle || ''} onChange={(e) => setData(d => ({ ...d, contactTitle: e.target.value }))} placeholder="VP of Social Impact" className="rounded-xl border-gray-200 font-body" data-testid="corp-contact-title" />
        </div>
        <div>
          <label className="text-sm font-medium text-[#111827] font-heading block mb-1.5">Estimated Annual Budget</label>
          <div className="grid grid-cols-3 gap-2">
            {['< $50K', '$50K-$500K', '$500K+'].map(b => (
              <button key={b} onClick={() => setData(d => ({ ...d, budget: b }))} className={`px-3 py-2.5 rounded-xl text-sm font-body transition-all border-2 ${data.budget === b ? 'border-transparent bg-gradient-to-r from-[#7C3AED] to-[#2563EB] text-white' : 'border-gray-100 bg-gray-50 text-gray-600 hover:border-gray-200'}`} data-testid={`budget-${b.replace(/[^a-zA-Z0-9]/g, '')}`}>
                {b}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
  return (
    <div className="space-y-5 text-center" data-testid="corp-step-confirm">
      <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#2563EB] flex items-center justify-center mx-auto"><Building2 className="w-8 h-8 text-white" /></div>
      <h2 className="text-xl font-heading font-semibold text-[#111827]">Application Submitted!</h2>
      <p className="text-sm text-gray-500 font-body max-w-sm mx-auto">Our enterprise team will review your application and reach out within 1-2 business days.</p>
      <div className="bg-gray-50 rounded-xl p-4 text-left">
        <p className="text-xs text-gray-400 font-body mb-2">Summary</p>
        <p className="text-sm font-heading font-medium text-[#111827]">{data.companyName || 'Your Company'}</p>
        <p className="text-xs text-gray-500 font-body">{data.contactName || 'Contact'} &middot; {data.interests?.slice(0, 2).join(', ') || 'General'}</p>
      </div>
    </div>
  );
}

/* ======================== MAIN ONBOARDING PAGE ======================== */
const pathConfig = { donor: 4, ngo: 5, volunteer: 3, corporate: 4 };
const pathIcons = { donor: Heart, ngo: Globe, volunteer: Users, corporate: Building2 };

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const userType = user?.type || 'donor';
  const totalSteps = pathConfig[userType] || 4;

  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [data, setData] = useState({});

  const isLastStep = step === totalSteps - 1;

  const next = () => {
    if (isLastStep) {
      updateUser({ onboarded: true, ...data });
      navigate('/dashboard');
    } else { setDirection(1); setStep(s => s + 1); }
  };
  const back = () => { setDirection(-1); setStep(s => Math.max(0, s - 1)); };

  const PathComponent = { donor: DonorPath, ngo: NGOPath, volunteer: VolunteerPath, corporate: CorporatePath }[userType] || DonorPath;
  const Icon = pathIcons[userType];

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center py-12 px-4" data-testid="onboarding-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-lg">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Icon className="w-5 h-5 text-[#7C3AED]" />
          <span className="text-sm font-medium text-[#7C3AED] font-heading capitalize">{userType} Onboarding</span>
        </div>
        <StepIndicator current={step} total={totalSteps} />

        <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] p-6 sm:p-8 overflow-hidden min-h-[360px] relative">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div key={step} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3, ease: 'easeInOut' }}>
              <PathComponent step={step} data={data} setData={setData} />
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="flex items-center justify-between mt-6">
          {step > 0 ? (
            <Button variant="ghost" onClick={back} className="rounded-full text-gray-500 hover:text-[#111827] gap-2" data-testid="onboard-back-btn">
              <ArrowLeft className="w-4 h-4" /> Back
            </Button>
          ) : (
            <Link to="/dashboard">
              <Button variant="ghost" className="rounded-full text-gray-400 hover:text-gray-600 text-sm" data-testid="onboard-skip-btn">Skip for now</Button>
            </Link>
          )}
          <Button onClick={next} className="bg-gradient-to-r from-[#7C3AED] to-[#2563EB] text-white rounded-full px-6 h-10 border-0 hover:shadow-lg transition-all gap-2" data-testid="onboard-next-btn">
            {isLastStep ? 'Go to Dashboard' : 'Continue'} <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
